import React, { useState, useCallback, useMemo } from "react";
import { LoadScriptNext, Autocomplete } from "@react-google-maps/api";
import FAWrapper from "components/vendors/fa-wrapper";
import { AppendFormControl } from "components/bootstrap/input-group-wrapper";
import { randomString } from "utils/helpers";

const GoogleAutocompleteWrapper = (props) => {
  const {
    location = {},
    placeholder,
    disabled,
    setLocation,
    className = "",
    onValidationError,
  } = props;
  const [autocomplete, setAutocomplete] = useState(null);
  const [validationError, setValidationError] = useState("");

  const handlePlaceChanged = useCallback(() => {
    const newLocation = updateLocation(location, autocomplete);
    // Simple validation: check if address exists and has content
    if (!newLocation.address?.trim()) {
      const errorMsg = "Please type in complete address";
      setValidationError(errorMsg);
      // Show toast error via callback
      onValidationError?.(errorMsg);
      return;
    }
    onValidationError?.("")
    setValidationError("");
    setLocation(newLocation);
  }, [location, autocomplete, setLocation, onValidationError]);

  const errorDisplay = useMemo(() => {
    if (!validationError) return null;
    return (
      <div className="validation-error text-danger mt-2">
        <small>
          <FAWrapper icon="exclamation-triangle" className="mr-1" />
          {validationError}
        </small>
      </div>
    );
  }, [validationError]);

  return (
    <div className="autocomplete-wrapper" key={location.name}>
      <LoadScriptNext
        googleMapsApiKey={
          typeof window == "undefined"
            ? ""
            : window.env.REACT_APP_GOOGLE_API_KEY
        }
        libraries={["places"]}
      >
        <Autocomplete
          onLoad={setAutocomplete}
          onPlaceChanged={handlePlaceChanged}
          options={{ types: ["geocode", "establishment"] }}
          fields={[
            "name",
            "formatted_address",
            "geometry",
            "address_component",
          ]}
        >
          <AppendFormControl
            icon="search"
            name={randomString()}
            defaultValue={location.name || ""}
            placeholder="Search for a place or an address"
            disabled={disabled}
            onChange={() => null}
          />
        </Autocomplete>
      </LoadScriptNext>
      {errorDisplay}
    </div>
  );
};

function updateLocation(location, autocomplete) {
  let data = {};
  data.id = location?.id || null;

  let google = autocomplete?.getPlace();
  let googleProps = google?.address_components;
  // Sometimes there aren't any results.
  let streetNumber = googleProps?.find((c) =>
    c?.types?.includes("street_number")
  );
  let route = googleProps?.find((c) => c?.types?.includes("route"));
  if (streetNumber) {
    data.name = google?.formatted_address;
    data.address =
      streetNumber && route
        ? streetNumber?.long_name + " " + route?.short_name
        : null;
    data.city = googleProps?.find((component) =>
      component?.types?.includes("locality")
    );
    data.state = googleProps?.find((component) =>
      component?.types?.includes("administrative_area_level_1")
    );
    data.country = googleProps?.find((component) =>
      component?.types?.includes("country")
    );
    data.postalCode = googleProps?.find((component) =>
      component?.types?.includes("postal_code")
    );
    data.latitude = google?.geometry?.location?.lat();
    data.longitude = google?.geometry?.location?.lng();

    data.city = data?.city ? data?.city?.long_name : "";
    data.state = data?.state ? data?.state?.long_name : "";
    data.country = data?.country ? data?.country?.long_name : "";
    data.postalCode = data?.postalCode ? data?.postalCode?.long_name : "";
  }
  return data;
}

export default GoogleAutocompleteWrapper;

// Docs
// https://react-google-maps-api-docs.netlify.app/#autocomplete
